import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, Scissors, ArrowRight, Phone, AlertCircle } from 'lucide-react';

// Braiding Services with actual prices
const braidingCategories = [
  {
    category: 'Knotless Braids',
    services: [
      { name: 'Large', price: '₱3,500' },
      { name: 'Medium', price: '₱4,000' },
      { name: 'Small', price: '₱5,000' },
    ]
  },
  {
    category: 'Box Braids',
    services: [
      { name: 'Large Box Braids', price: '₱3,000' },
      { name: 'Medium Box Braids', price: '₱3,500' },
      { name: 'Small Box Braids', price: '₱4,000' },
    ]
  },
  {
    category: 'Island Twist',
    services: [
      { name: 'Large Twist', price: '₱3,500' },
      { name: 'Medium Twist', price: '₱4,000' },
      { name: 'Small Twist', price: '₱4,000' },
    ]
  },
  {
    category: 'Boho Braids',
    services: [
      { name: 'Medium Twist', price: '₱4,000' },
      { name: 'Small', price: '₱4,500' },
    ]
  },
  {
    category: 'Natural Cornrows',
    services: [
      { name: 'Cornrows', price: '₱1,000' },
      { name: 'Stitch Cornrows', price: '₱1,500' },
      { name: 'Freestyling', price: 'Price Varies' },
    ]
  },
  {
    category: 'Feed In Cornrow',
    services: [
      { name: 'Feed in Cornrows', price: '₱1,500' },
      { name: 'French Curls', price: '₱4,000' },
      { name: 'Pick and Drop', price: '₱3,000' },
      { name: 'Crochet Braids', price: '₱2,500' },
    ]
  },
  {
    category: 'Natural Twist',
    services: [
      { name: 'Medium Twist', price: '₱2,500' },
      { name: 'Mini Twist Long Hair', price: '₱3,000' },
    ]
  },
  {
    category: 'Fulani Braids',
    services: [
      { name: 'Fulani Natural', price: '₱1,500' },
      { name: 'Fulani Extension', price: '₱3,000' },
    ]
  },
  {
    category: 'Locs',
    services: [
      { name: 'Half Head', price: '₱1,500' },
      { name: 'Full Head', price: '₱2,500' },
    ]
  },
  {
    category: 'Sew In Weave',
    services: [
      { name: 'Sew in Weave', price: '₱2,500' },
      { name: 'Frontal Installation', price: '₱1,500' },
    ]
  },
];

// Hair Care Services
const hairCareServices = [
  { name: 'Hair Wash', price: '₱350' },
  { name: 'Natural Take Down', price: '₱300' },
  { name: 'Extension Take Down', price: '₱600' },
  { name: 'Hair Relaxing', price: '₱500' },
  { name: 'Curl Ends', price: '₱500' },
];

// Barber/Haircut Services
const barberServices = [
  { 
    name: 'Premium Haircut', 
    description: 'Haircuts, Massage, Facial & Drink',
    price: '₱1,000' 
  },
  { 
    name: 'Gold Package', 
    description: 'Haircut, Head Massage & Drink',
    price: '₱750' 
  },
  { 
    name: 'Hair Cut', 
    description: 'Professional precision cut',
    price: '₱500' 
  },
  { 
    name: 'Hair Cut + Colour', 
    description: 'Cut with color styling',
    price: '₱1,000' 
  },
  { 
    name: 'Shave / Carving', 
    description: 'Clean shave or design carving',
    price: '₱250' 
  },
  { 
    name: 'Bleaching + Colouring', 
    description: 'Full bleach and color treatment',
    price: '₱1,500' 
  },
];

const ServiceCard = ({ category, services, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="bg-surface border border-gold/20 rounded-2xl p-6 hover:border-pink/50 transition-all duration-300"
  >
    <h3 className="font-serif text-xl text-gold-light mb-4 border-b border-gold/20 pb-3">{category}</h3>
    <div className="space-y-3">
      {services.map((service, idx) => (
        <div key={idx} className="flex justify-between items-center">
          <span className="text-[#F5F5F0] font-sans">{service.name}</span>
          <span className="text-pink font-serif font-semibold">{service.price}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const BarberCard = ({ service, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="bg-surface border border-gold/20 rounded-2xl p-6 hover:border-pink/50 transition-all duration-300"
  >
    <div className="flex justify-between items-start mb-2">
      <h3 className="font-serif text-xl text-gold-light">{service.name}</h3>
      <span className="text-pink font-serif font-semibold text-xl">{service.price}</span>
    </div>
    <p className="text-[#A3A3A3] font-sans text-sm">{service.description}</p>
  </motion.div>
);

export const Services = () => {
  return (
    <div className="page-transition pt-24">
      {/* Hero */}
      <section className="py-20 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-script text-5xl md:text-7xl text-gold mb-4"
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-sans text-[#A3A3A3] text-lg max-w-2xl mx-auto"
          >
            Luxury styling crafted for those who expect nothing less than perfection
          </motion.p>
          
          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 flex items-center justify-center gap-2 text-gold"
          >
            <i className="fab fa-whatsapp text-xl text-green-500"></i>
            <a href="https://wa.me/639166744137" target="_blank" rel="noopener noreferrer" className="font-serif text-xl hover:text-pink transition-colors">
              09166744137
            </a>
          </motion.div>
        </div>
      </section>

      {/* Braiding Services */}
      <section className="py-20 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4 mb-12"
          >
            <Sparkles className="w-8 h-8 text-pink" />
            <h2 className="font-script text-4xl md:text-5xl text-gold">Braiding Services</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {braidingCategories.map((cat, index) => (
              <ServiceCard key={cat.category} category={cat.category} services={cat.services} index={index} />
            ))}
          </div>

          {/* Hair Care Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <h3 className="font-script text-3xl text-gold mb-8">Hair Care Services</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {hairCareServices.map((service, index) => (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-[#0a0a0a] border border-gold/20 rounded-xl p-4 text-center hover:border-pink/50 transition-all"
                >
                  <span className="text-[#F5F5F0] font-sans block mb-2">{service.name}</span>
                  <span className="text-pink font-serif font-semibold">{service.price}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-pink/10 border border-pink/30 rounded-xl p-6"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-pink flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-serif text-lg text-gold-light mb-2">Things to Note</h4>
                <ul className="text-[#A3A3A3] font-sans space-y-1">
                  <li>• Prices do not include extensions</li>
                  <li>• Extra length may increase price</li>
                  <li>• Too much hair may increase price for locs</li>
                  <li>• NO CREDIT - Payment required upfront</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Barber Services */}
      <section className="py-20 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4 mb-12"
          >
            <Scissors className="w-8 h-8 text-pink" />
            <h2 className="font-script text-4xl md:text-5xl text-gold">Hair Cut Services</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {barberServices.map((service, index) => (
              <BarberCard key={service.name} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-surface">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-script text-4xl md:text-6xl text-gold mb-6"
          >
            Ready to Book?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-sans text-[#A3A3A3] text-lg mb-6"
          >
            Secure your appointment and experience the luxury that defines BraidsByMel
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/contact"
              className="btn-gold px-10 py-4 rounded-full font-serif text-lg inline-flex items-center gap-2 group"
              data-testid="services-book-btn"
            >
              Book Your Appointment
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="https://wa.me/639166744137"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-green-500 text-green-500 px-10 py-4 rounded-full font-serif text-lg inline-flex items-center gap-2 hover:bg-green-500 hover:text-white transition-all"
              data-testid="services-whatsapp-btn"
            >
              <i className="fab fa-whatsapp text-xl"></i>
              WhatsApp Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
